import { ConfirmDialog } from "@/components/ConfirmDialog";
import { inventoryApi } from "@/features/inventory/api";
import { categoriesKeys, furnitureKeys } from "@/features/inventory/queryKeys";
import { stagingsApi } from "@/features/stagings/api";
import { stagingsKeys } from "@/features/stagings/queryKeys";
import { templatesApi } from "@/features/templates/api";
import { templatesKeys } from "@/features/templates/queryKeys";
import { baseUrl } from "@/lib/http";
import { useInfinitePagedList } from "@/lib/useInfinitePagedList";
import {
  type FileProperties,
  type Furniture,
  type FurnitureItem,
  FurnitureStatus,
  type Room,
  type RoomGenericItem,
  Staging,
  STAGING_STATUS,
  StagingStatus,
  type StorageLocation,
} from "@/types/api";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import NoteIcon from "@mui/icons-material/Note";
import PublicIcon from "@mui/icons-material/Public";
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReservationHistoryDialog } from "../inventory/ReservationHistoryDialog";
import { SearchHelpDialog } from "../inventory/SearchHelpDialog";
import { roomKeys, roomsKeys } from "../staging-view/queryKeys";
import { roomsApi, unreserveFurnitureItems, updateFurnitureItemsLocations } from "./api";

// ─── helpers ───────────────────────────────────────────────────────────────

function thumb(path?: string | null) {
  return path ? `${baseUrl}${path}` : null;
}

// ─── card size (persisted in cookie) ────────────────────────────────────────

const CARD_SCALE_COOKIE = "roomBuilderCardScale";
const CARD_SCALE_MIN = 0.5;
const CARD_SCALE_MAX = 2;
const CARD_SCALE_DEFAULT = 1;

function readCardScaleCookie(): number {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CARD_SCALE_COOKIE}=([^;]*)`));
  if (!match) return CARD_SCALE_DEFAULT;
  const value = parseFloat(decodeURIComponent(match[1]));
  if (isNaN(value)) return CARD_SCALE_DEFAULT;
  return Math.min(CARD_SCALE_MAX, Math.max(CARD_SCALE_MIN, value));
}

function writeCardScaleCookie(scale: number) {
  // persist for ~1 year
  const maxAge = 60 * 60 * 24 * 365;
  document.cookie = `${CARD_SCALE_COOKIE}=${
    encodeURIComponent(String(scale))
  }; path=/; max-age=${maxAge}`;
}

function bestThumb(imageFile?: FileProperties | null): string | null {
  if (!imageFile) return null;
  return thumb(imageFile.thumbnailPath280) ?? thumb(imageFile.thumbnailPath110)
    ?? thumb(imageFile.relativePath);
}

function dimLabel(f: Furniture) {
  const parts: string[] = [];
  if (f.width != null) parts.push(`${f.width}W`);
  if (f.height != null) parts.push(`${f.height}H`);
  if (f.length != null) parts.push(`${f.length}D`);
  return parts.join(" ") || "";
}

// ─── quantity chip ─────────────────────────────────────────────────────────

/**
 * Count shown on a card standing for several identical items — a placeholder
 * covering N slots, or N reserved items of the same furniture in the same state.
 * Hidden for a single item, where the card already speaks for itself.
 */
function QuantityChip({ quantity, onDark }: { quantity: number; onDark?: boolean }) {
  if (quantity <= 1) return null;
  return (
    <Box
      sx={{
        px: 0.75,
        borderRadius: 1,
        flexShrink: 0,
        alignSelf: "flex-start",
        bgcolor: onDark ? "rgba(255,255,255,0.9)" : "primary.main",
        color: onDark ? "rgba(0,0,0,0.87)" : "primary.contrastText",
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.6,
      }}
    >
      ×{quantity}
    </Box>
  );
}

// ─── placeholder card ──────────────────────────────────────────────────────

interface PlaceholderCardProps {
  item: RoomGenericItem;
  showNotes: boolean;
  scale: number;
  isSelected?: boolean;
  onDelete: () => void;
  onCheck: (checked: boolean) => void;
  onEditNotes: () => void;
  onSelectFurniture: () => void;
}

function PlaceholderCard(
  { item, showNotes, scale, isSelected, onDelete, onCheck, onEditNotes, onSelectFurniture }:
    PlaceholderCardProps,
) {
  const furniture = item.replaceItemInfo?.furnitureInfo;
  const imgSrc = bestThumb(furniture?.imageFile);
  const cardWidth = 250 * scale;
  const imgHeight = 120 * scale;

  return (
    <Paper
      variant="outlined"
      sx={{
        width: { xs: "100%", sm: cardWidth },
        maxWidth: { xs: "100%", sm: cardWidth },
        minHeight: 250 * scale,
        flexShrink: 0,
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        ...(isSelected && {
          borderColor: "primary.main",
          borderWidth: 2,
          boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}33`,
        }),
      }}
    >
      {/* selected indicator */}
      {isSelected && (
        <Box sx={{ bgcolor: "primary.main", px: 1, py: 0.25 }}>
          <Typography variant="caption" sx={{ color: "white", fontSize: 10 }}>
            Will be replaced
          </Typography>
        </Box>
      )}

      {/* actions row */}
      <Box sx={{ display: "flex", alignItems: "center", px: 0.5, py: 0.25, gap: 0.5 }}>
        <Checkbox
          size="small"
          checked={item.checked}
          onChange={(e) => onCheck(e.target.checked)}
          sx={{ p: 0 }}
        />
        <IconButton size="small" onClick={onEditNotes} sx={{ p: 0 }}>
          <EditIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onDelete} sx={{ p: 0, color: "error.main" }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* image / select area */}
      {furniture && imgSrc
        ? (
          <Box
            sx={{
              width: "100%",
              height: imgHeight,
              backgroundImage: `url(${imgSrc})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        )
        : furniture
        ? (
          <Box
            sx={{
              width: "100%",
              height: imgHeight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.100",
            }}
          >
            <ImageIcon color="disabled" />
          </Box>
        )
        : (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onSelectFurniture();
            }}
            sx={{
              width: "100%",
              height: imgHeight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.50",
              cursor: "pointer",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Select
            </Typography>
          </Box>
        )}

      {/* item name / dimensions / slot count */}
      <Box
        sx={{
          px: 1,
          py: 0.5,
          bgcolor: furniture ? "rgba(0,0,0,0.65)" : "transparent",
          flex: 1,
          display: "flex",
          gap: 0.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: furniture ? "white" : "text.secondary",
              display: "block",
              lineHeight: 1.2,
            }}
          >
            {furniture?.name ?? item.name}
          </Typography>
          {furniture && (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", fontSize: 10 }}>
              {dimLabel(furniture)}
            </Typography>
          )}
        </Box>
        <QuantityChip quantity={item.quantity} onDark={!!furniture} />
      </Box>

      {/* notes section (when furniture notes toggle is on) */}
      {showNotes && (
        <Box sx={{ px: 1, py: 0.5, bgcolor: "grey.200", minHeight: 28 }}>
          <Typography variant="caption" color="text.secondary">
            <span style={{ fontSize: 10 }}>{item.notes}</span>
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

// ─── reserved furniture card ───────────────────────────────────────────────

interface ReservedCardProps {
  item: FurnitureItem;
  /** How many identical reserved items this card stands for. */
  quantity: number;
  checked: boolean;
  /** Some but not all of the grouped items are checked. */
  indeterminate?: boolean;
  showNotes: boolean;
  scale: number;
  onDelete: () => void;
  onCheck: (checked: boolean) => void;
  onEditNotes: () => void;
  onImageClick?: () => void;
  statusBanner?: React.ReactNode;
}

function ReservedCard(
  {
    item,
    quantity,
    checked,
    indeterminate,
    showNotes,
    scale,
    onDelete,
    onCheck,
    onEditNotes,
    onImageClick,
    statusBanner,
  }: ReservedCardProps,
) {
  const furniture = item.furnitureInfo;
  const imgSrc = bestThumb(furniture?.imageFile);
  const cardWidth = 250 * scale;
  const imgHeight = 250 * scale;

  return (
    <Paper
      variant="outlined"
      sx={{
        width: { xs: "100%", sm: cardWidth },
        maxWidth: { xs: "100%", sm: cardWidth },
        minHeight: (showNotes ? 240 : 212) * scale,
        flexShrink: 0,
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", px: 0.5, py: 0.25, gap: 0.5 }}>
        <Checkbox
          size="small"
          checked={checked}
          indeterminate={indeterminate}
          onChange={(e) => onCheck(e.target.checked)}
          sx={{ p: 0 }}
        />
        <IconButton size="small" onClick={onEditNotes} sx={{ p: 0 }}>
          <EditIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onDelete} sx={{ p: 0, color: "error.main" }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
      {imgSrc
        ? (
          <Box
            sx={{
              width: "100%",
              height: imgHeight,
              backgroundImage: `url(${imgSrc})`,
              backgroundSize: "contain",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              cursor: onImageClick ? "pointer" : "default",
            }}
            onClick={onImageClick}
          />
        )
        : (
          <Box
            sx={{
              width: "100%",
              height: imgHeight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.100",
              cursor: onImageClick ? "pointer" : "default",
            }}
            onClick={onImageClick}
          >
            <ImageIcon color="disabled" />
          </Box>
        )}
      <Box
        sx={{ px: 1, py: 0.5, bgcolor: "rgba(0,0,0,0.65)", flex: 1, display: "flex", gap: 0.5 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: "white", display: "block", lineHeight: 1.2 }}>
            {furniture?.name ?? `Item #${item.id}`}
          </Typography>
          {furniture && (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", fontSize: 10 }}>
              {dimLabel(furniture)}
            </Typography>
          )}
        </Box>
        <QuantityChip quantity={quantity} onDark />
      </Box>
      <Box sx={{ bgcolor: "rgba(0,0,0,0.65)" }}>
        {showNotes && (
          <Box sx={{ px: 1, py: 0.5, bgcolor: "grey.200", minHeight: 28 }}>
            <Typography variant="caption" color="text.secondary">
              <span style={{ fontSize: 10 }}>{item.roomNotes}</span>
            </Typography>
          </Box>
        )}
        {statusBanner}
      </Box>
    </Paper>
  );
}

// ─── reserved item grouping ────────────────────────────────────────────────

type ReservedLocation = "atLocation" | "inLocker" | "atOtherLocation";

type LocationCounts = Record<ReservedLocation, number>;

interface ReservedGroup {
  key: string;
  items: FurnitureItem[];
  counts: LocationCounts;
}

/** Where one reserved item physically is, relative to the room being built. */
function reservedLocation(item: FurnitureItem, roomId: number): ReservedLocation {
  if (item.room === roomId) return "atLocation";
  if (item.room == null) return "inLocker";
  return "atOtherLocation";
}

const LOCATION_LABELS: Record<ReservedLocation, string> = {
  atLocation: "At location",
  inLocker: "In locker",
  atOtherLocation: "At other location",
};

// One brand colour per state, as the style guide pairs them: Wild Sage for
// "here", Camel for "somewhere else", Eucalyptus for the locker in between.
// Each band carries its own text colour because the light brand tones need
// Cast Iron on top rather than white.
const LOCATION_COLORS: Record<ReservedLocation, { bg: string; text: string }> = {
  atLocation: { bg: "success.main", text: "success.contrastText" },
  inLocker: { bg: "info.main", text: "info.contrastText" },
  atOtherLocation: { bg: "warning.main", text: "warning.contrastText" },
};

// Fixed order so a card's breakdown always reads the same way.
const LOCATION_ORDER: ReservedLocation[] = ["atLocation", "inLocker", "atOtherLocation"];

/** Larger first; a missing value sorts after any recorded one. */
function compareDescNullsLast(a: number | null | undefined, b: number | null | undefined): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return b - a;
}

/**
 * Orders reserved cards by furniture width, then height (both largest first),
 * then name. Furniture with no recorded dimension sorts after furniture that
 * has one, and the name breaks ties so the grid keeps the same order between
 * renders. Mirrors the picker's `sort=size` order on the server.
 */
function compareBySize(a: ReservedGroup, b: ReservedGroup): number {
  const aFurniture = a.items[0].furnitureInfo;
  const bFurniture = b.items[0].furnitureInfo;
  return compareDescNullsLast(aFurniture?.width, bFurniture?.width)
    || compareDescNullsLast(aFurniture?.height, bFurniture?.height)
    || (aFurniture?.name ?? "").localeCompare(bFurniture?.name ?? "", undefined, { numeric: true });
}

/**
 * Collapses a room's reserved items into one card per furniture and note. Items
 * scattered across the locker, this room and other stagings stay on one card —
 * the card carries the per-location breakdown instead of splitting. The note is
 * part of the key because a card edits the note of everything it covers. Cards
 * come back ordered by width, then height, both largest first, then name.
 */
function groupReservedItems(items: FurnitureItem[], roomId: number): ReservedGroup[] {
  const groups = new Map<string, ReservedGroup>();
  for (const item of items) {
    const key = [item.furniture, item.roomNotes ?? ""].join("|");
    let group = groups.get(key);
    if (!group) {
      group = { key, items: [], counts: { atLocation: 0, inLocker: 0, atOtherLocation: 0 } };
      groups.set(key, group);
    }
    group.items.push(item);
    group.counts[reservedLocation(item, roomId)]++;
  }
  return [...groups.values()].sort(compareBySize);
}

/**
 * The card's footer: one band per location the group's items are in, so a card
 * standing for several items says where each of them actually is.
 */
function LocationBreakdown({ counts }: { counts: LocationCounts }) {
  return (
    <>
      {LOCATION_ORDER.filter((location) => counts[location] > 0).map((location) => (
        <Box
          key={location}
          sx={{
            bgcolor: LOCATION_COLORS[location].bg,
            px: 0.5,
            py: 0.25,
            textAlign: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: LOCATION_COLORS[location].text, fontSize: 10, fontWeight: 600 }}
          >
            {counts[location]} {LOCATION_LABELS[location]}
          </Typography>
        </Box>
      ))}
    </>
  );
}

// ─── remove reserved items dialog ──────────────────────────────────────────

interface RemoveReservedDialogProps {
  group: ReservedGroup;
  isPending: boolean;
  onClose: () => void;
  onConfirm: (quantity: number) => void;
}

/** Removes some or all of a grouped card's items; defaults to the whole card. */
function RemoveReservedDialog({ group, isPending, onClose, onConfirm }: RemoveReservedDialogProps) {
  const max = group.items.length;
  const [quantity, setQuantity] = useState(max);
  const name = group.items[0].furnitureInfo?.name ?? `Item #${group.items[0].id}`;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Remove {name}</DialogTitle>
      <DialogContent>
        {max > 1
          ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                How many of the {max} should go back to the inventory?
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                value={quantity}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v > 0) setQuantity(Math.min(v, max));
                }}
                inputProps={{ min: 1, max }}
              />
            </>
          )
          : (
            <Typography variant="body2">
              Are you sure you want to remove this item from the room?
            </Typography>
          )}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
          Any placeholder these items were filling comes back, notes included.
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={isPending}
            onClick={() => onConfirm(quantity)}
          >
            Remove
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ─── add item dialog ───────────────────────────────────────────────────────

const QUICK_QUANTITIES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

interface AddItemDialogProps {
  open: boolean;
  maxQuantity: number;
  /** Pre-selected quantity — the slots a pending placeholder still has to fill. */
  defaultQuantity: number;
  onClose: () => void;
  onAdd: (quantity: number) => void;
  isPending: boolean;
}

function AddItemDialog(
  { open, maxQuantity, defaultQuantity, onClose, onAdd, isPending }: AddItemDialogProps,
) {
  const [quantity, setQuantity] = useState(defaultQuantity);

  // Each furniture suggests its own starting quantity, so reopening the dialog
  // for a different one starts from that suggestion rather than the last pick.
  useEffect(() => {
    if (open) setQuantity(defaultQuantity);
  }, [open, defaultQuantity]);

  function handleQuantityInput(raw: string) {
    const v = parseInt(raw, 10);
    if (!isNaN(v) && v > 0) setQuantity(Math.min(v, maxQuantity));
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add item</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Select a quantity below
        </Typography>
        <TextField
          type="number"
          fullWidth
          size="small"
          value={quantity}
          onChange={(e) => handleQuantityInput(e.target.value)}
          inputProps={{ min: 1, max: maxQuantity }}
          sx={{ mb: 2 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          or choose between the quantities below
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 0.75, mb: 3 }}>
          {QUICK_QUANTITIES.map((n) => (
            <Button
              key={n}
              variant={quantity === n ? "contained" : "outlined"}
              size="small"
              disabled={n > maxQuantity}
              onClick={() => setQuantity(n)}
              sx={{ minWidth: 0, px: 0 }}
            >
              {n}
            </Button>
          ))}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={isPending} onClick={() => onAdd(quantity)}>
            Add
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ─── inventory panel ───────────────────────────────────────────────────────

const INVENTORY_PAGE_SIZE = 20;

interface InventoryPanelProps {
  staging: Staging;
  selectedRoom: Room | null;
  onReserved: () => void;
  categoryId: number | "";
  onCategoryChange: (id: number | "") => void;
  pendingPlaceholder?: RoomGenericItem | null;
}

function InventoryPanel(
  { staging, selectedRoom, onReserved, categoryId, onCategoryChange, pendingPlaceholder }:
    InventoryPanelProps,
) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pendingFurniture, setPendingFurniture] = useState<
    { id: number; available: number } | null
  >(null);
  const [preview, setPreview] = useState<{ name: string; src: string } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [helpOpen, setHelpOpen] = useState(false);

  const categories = useQuery({
    queryKey: categoriesKeys.all,
    queryFn: () => inventoryApi.listCategories(),
  });

  const furniturePages = useInfinitePagedList<Furniture>({
    queryKey: furnitureKeys.builderList(categoryId, debouncedSearch),
    fetchPage: ({ skip, limit }) =>
      inventoryApi.listFurniture({
        search: debouncedSearch || undefined,
        categoryId: categoryId !== "" ? categoryId : undefined,
        sort: "size",
        limit,
        skip,
      }),
    pageSize: INVENTORY_PAGE_SIZE,
  });

  const furniture = furniturePages.items;
  const sentinelRef = furniturePages.sentinelRef;

  const qc = useQueryClient();

  const reserve = useMutation({
    mutationFn: (
      { furnitureId, quantity, notes }: { furnitureId: number; quantity: number; notes?: string },
    ) =>
      roomsApi.reserveFurniture({
        furnitureId,
        roomId: selectedRoom!.id,
        quantity,
        notes,
        // The backend takes the filled slots off the placeholder and leaves any
        // surplus behind as a smaller placeholder to fill with something else.
        placeholderId: pendingPlaceholder?.id ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roomKeys.detail(selectedRoom?.id) });
      void qc.invalidateQueries({ queryKey: furnitureKeys.lists() });
      onReserved();
    },
  });

  // A reservation can never overshoot the stock on hand, nor the slots the
  // placeholder it is filling still has open.
  const availableToReserve = pendingFurniture?.available ?? 1;
  const openSlots = pendingPlaceholder?.quantity ?? 0;
  const maxReservable = openSlots > 0
    ? Math.min(availableToReserve, openSlots)
    : availableToReserve;

  function handleSearchChange(value: string) {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 300);
  }

  function countItems(f: Furniture, currentStagingId: number) {
    const items = f.items ?? [];
    const total = items.length;
    const inLocker = items.filter((i) => i.room == null).length;
    const atOther =
      items.filter((i) => i.room != null && !!i.roomInfo && i.roomInfo.staging != currentStagingId)
        .length;
    const availableFurnitureStatuses = [
      FurnitureStatus.Serviceable,
      FurnitureStatus.NeedsRepairAvailable,
    ];
    const availableStagingStatuses = [
      StagingStatus.Destaging,
      StagingStatus.Sold,
      StagingStatus.Completed,
    ];
    const available = items.filter((i) => {
      if (!availableFurnitureStatuses.includes(i.status)) {
        return false;
      }

      if (!i.reservationInfo) {
        return true;
      }

      return i.reservationInfo.stagingInfo != null
        && availableStagingStatuses.includes(i.reservationInfo.stagingInfo.status);
    }).length;
    return { total, inLocker, atOther, available };
  }

  const isStagingRoomBuilderCompleted = () => {
    const completedStatuses = [
      StagingStatus.Sold,
      StagingStatus.Destaging,
      StagingStatus.Completed,
    ];
    return completedStatuses.includes(staging.status ?? StagingStatus.Upcoming);
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {/* pinned header — the filters stay put while the furniture list scrolls */}
      <Box sx={{ flexShrink: 0, p: 1, pb: 0, bgcolor: "background.paper" }}>
        {/* category filter */}
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={categoryId}
          onChange={(e) => onCategoryChange(e.target.value)}
          sx={{ mb: 1 }}
        >
          <MenuItem value="">
            <em>Category</em>
          </MenuItem>
          {(categories.data ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </Select>

        {/* locate item search */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <IconButton onClick={() => setHelpOpen(true)} color="primary">
            <InfoIcon />
          </IconButton>
          <TextField
            size="small"
            fullWidth
            placeholder="Locate item"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              endAdornment: search
                ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearch("");
                      setDebouncedSearch("");
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )
                : undefined,
            }}
          />
        </Box>
      </Box>

      <SearchHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* scrolling furniture list */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 1, pb: 1 }}>
        {furniturePages.isLoading && <CircularProgress size={20} />}

        {furniture.map((f) => {
          const { inLocker, atOther, available, total } = countItems(f, staging.id);

          if (available === 0) {
            return <></>;
          }

          const imgSrc = thumb(f.imageFile?.thumbnailPath110);
          return (
            <Box key={f.id} sx={{ mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                {f.name}
              </Typography>
              {f.length != null || f.width != null || f.height != null
                ? (
                  <Typography variant="caption" color="text.secondary">
                    {dimLabel(f)}
                  </Typography>
                )
                : null}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                {imgSrc
                  ? (
                    <Box
                      component="img"
                      src={imgSrc}
                      alt={f.name}
                      onClick={() =>
                        setPreview({ name: f.name, src: thumb(f.imageFile?.relativePath)! })}
                      sx={{
                        width: 56,
                        height: 56,
                        objectFit: "cover",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        cursor: "pointer",
                      }}
                    />
                  )
                  : <Box sx={{ width: 56, height: 56, bgcolor: "grey.100", borderRadius: 1 }} />}
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                    <Tooltip title="In locker">
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <WarehouseIcon fontSize="small" color="action" />
                        <Typography variant="caption">{inLocker}</Typography>
                      </Stack>
                    </Tooltip>
                    <Tooltip title="At other location">
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <PublicIcon fontSize="small" color="action" />
                        <Typography variant="caption">{atOther}</Typography>
                      </Stack>
                    </Tooltip>
                  </Stack>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {available} / {total}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={!selectedRoom || available === 0 || reserve.isPending
                    || isStagingRoomBuilderCompleted()}
                  onClick={() => setPendingFurniture({ id: f.id, available })}
                  sx={{ border: "1px solid", borderColor: "primary.main" }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          );
        })}

        {/* virtual scroll sentinel */}
        <Box ref={sentinelRef} sx={{ py: 0.5, display: "flex", justifyContent: "center" }}>
          {furniturePages.isFetchingNextPage && <CircularProgress size={16} />}
        </Box>

        {!furniturePages.isLoading && furniture.length === 0 && (
          <Typography color="text.secondary" variant="body2">
            No items found.
          </Typography>
        )}
      </Box>

      <AddItemDialog
        open={pendingFurniture !== null}
        maxQuantity={maxReservable}
        defaultQuantity={openSlots > 0 ? maxReservable : 1}
        onClose={() => setPendingFurniture(null)}
        onAdd={(quantity) => {
          reserve.mutate({
            furnitureId: pendingFurniture!.id,
            quantity,
            notes: pendingPlaceholder?.notes,
          });
          setPendingFurniture(null);
        }}
        isPending={reserve.isPending}
      />
      {preview && (
        <ImagePreviewDialog
          name={preview.name}
          src={preview.src}
          onClose={() => setPreview(null)}
        />
      )}
    </Box>
  );
}

// ─── rooms sidebar panel ───────────────────────────────────────────────────

interface RoomsSidebarProps {
  stagingId: number;
  rooms: Room[];
  selectedRoomId: number | null;
  onSelect: (room: Room) => void;
  onRefresh: () => void;
}

function RoomsSidebar(
  { stagingId, rooms, selectedRoomId, onSelect, onRefresh }: RoomsSidebarProps,
) {
  const [addOpen, setAddOpen] = useState(false);
  const [renameRoom, setRenameRoom] = useState<Room | null>(null);
  const [confirmDeleteRoom, setConfirmDeleteRoom] = useState<Room | null>(null);
  const qc = useQueryClient();

  const del = useMutation({
    mutationFn: roomsApi.remove,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roomsKeys.list(stagingId) });
      setConfirmDeleteRoom(null);
      onRefresh();
    },
  });

  return (
    <Box sx={{ flex: 1, overflow: "auto" }}>
      {rooms.map((room) => (
        <Box
          key={room.id}
          onClick={() => onSelect(room)}
          sx={{
            display: "flex",
            alignItems: "center",
            px: 1,
            py: 0.75,
            cursor: "pointer",
            bgcolor: selectedRoomId === room.id ? "action.selected" : "transparent",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Typography
            variant="body2"
            sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {room.name}
          </Typography>
          <Tooltip title="Rename">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setRenameRoom(room);
              }}
              sx={{ p: 0.25 }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDeleteRoom(room);
              }}
              sx={{ p: 0.25, color: "error.main" }}
            >
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
      <ConfirmDialog
        open={confirmDeleteRoom !== null}
        title="Delete room"
        message={`Are you sure you want to delete "${confirmDeleteRoom?.name}"?`}
        onClose={() => setConfirmDeleteRoom(null)}
        isPending={del.isPending}
        onConfirm={() => {
          if (confirmDeleteRoom) del.mutate(confirmDeleteRoom.id);
        }}
      />

      <Divider sx={{ my: 1 }} />

      <Box sx={{ px: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          fullWidth
          onClick={() => setAddOpen(true)}
          sx={{ borderStyle: "dashed" }}
        >
          Add Room
        </Button>
      </Box>

      <AddRoomDialog
        open={addOpen}
        stagingId={stagingId}
        onClose={() => setAddOpen(false)}
        onCreated={(room) => {
          setAddOpen(false);
          void qc.invalidateQueries({ queryKey: roomsKeys.list(stagingId) });
          onSelect(room);
        }}
      />

      {renameRoom && (
        <RenameRoomDialog
          room={renameRoom}
          onClose={() => setRenameRoom(null)}
          onRenamed={() => {
            setRenameRoom(null);
            void qc.invalidateQueries({ queryKey: roomsKeys.list(stagingId) });
          }}
        />
      )}
    </Box>
  );
}

// ─── add room dialog ───────────────────────────────────────────────────────

interface AddRoomDialogProps {
  open: boolean;
  stagingId: number;
  onClose: () => void;
  onCreated: (room: Room) => void;
}

function AddRoomDialog({ open, stagingId, onClose, onCreated }: AddRoomDialogProps) {
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState<number | "">("");

  const templates = useQuery({
    queryKey: templatesKeys.lists(),
    queryFn: () => templatesApi.list(),
  });

  const create = useMutation({
    mutationFn: () =>
      roomsApi.create({
        stagingId,
        name,
        templateId: templateId !== "" ? templateId : null,
      }),
    onSuccess: (room) => {
      setName("");
      setTemplateId("");
      onCreated(room);
    },
  });

  function handleClose() {
    setName("");
    setTemplateId("");
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Room</DialogTitle>
      <DialogContent>
        <TextField
          label="ROOM NAME"
          required
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />
        <Select
          displayEmpty
          fullWidth
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          size="small"
          sx={{ mb: 3 }}
        >
          <MenuItem value="">
            <em>Select a template (Optional)</em>
          </MenuItem>
          {(templates.data ?? []).map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </Select>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Create
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ─── rename room dialog ────────────────────────────────────────────────────

interface RenameRoomDialogProps {
  room: Room;
  onClose: () => void;
  onRenamed: () => void;
}

function RenameRoomDialog({ room, onClose, onRenamed }: RenameRoomDialogProps) {
  const [name, setName] = useState(room.name);

  const update = useMutation({
    mutationFn: () => roomsApi.update(room.id, { name, notes: room.notes }),
    onSuccess: onRenamed,
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Rename Room</DialogTitle>
      <DialogContent>
        <TextField
          label="Room name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 1, mb: 3 }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!name.trim() || update.isPending}
            onClick={() => update.mutate()}
          >
            Save
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ─── room notes dialog ─────────────────────────────────────────────────────

interface RoomNotesDialogProps {
  room: Room;
  onClose: () => void;
  onSaved: () => void;
}

function RoomNotesDialog({ room, onClose, onSaved }: RoomNotesDialogProps) {
  const [notes, setNotes] = useState(room.notes ?? "");
  const MAX = 500;

  const update = useMutation({
    mutationFn: () => roomsApi.update(room.id, { name: room.name, notes }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Room Notes</DialogTitle>
      <DialogContent>
        <TextField
          label="Notes"
          multiline
          minRows={4}
          fullWidth
          inputProps={{ maxLength: MAX }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          helperText={`${notes.length} / ${MAX}`}
          sx={{ mt: 1, mb: 3 }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={update.isPending} onClick={() => update.mutate()}>
            Save
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ─── placeholder notes dialog ──────────────────────────────────────────────

interface PlaceholderNotesDialogProps {
  roomId: number;
  item: RoomGenericItem;
  onClose: () => void;
  onSaved: () => void;
}

function PlaceholderNotesDialog({ roomId, item, onClose, onSaved }: PlaceholderNotesDialogProps) {
  const [notes, setNotes] = useState(item.notes ?? "");
  const MAX = 300;

  const update = useMutation({
    mutationFn: () =>
      roomsApi.updateItem(roomId, item.id, {
        name: item.name,
        notes,
        quantity: item.quantity,
        checked: item.checked,
        replaceItem: item.replaceItem,
        furnitureCategory: item.furnitureCategory,
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Stuff Notes — {item.name}</DialogTitle>
      <DialogContent>
        <TextField
          label="Notes"
          multiline
          minRows={4}
          fullWidth
          inputProps={{ maxLength: MAX }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          helperText={`${notes.length} / ${MAX}`}
          sx={{ mt: 1, mb: 3 }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={update.isPending} onClick={() => update.mutate()}>
            Save
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ─── reserved item notes dialog ───────────────────────────────────────────

interface ReservedNotesDialogProps {
  roomId: number;
  group: ReservedGroup;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Edits the note shared by a grouped card. The note is part of the grouping key,
 * so every item on the card carries the same one and the edit applies to all.
 */
function ReservedNotesDialog({ roomId, group, onClose, onSaved }: ReservedNotesDialogProps) {
  const item = group.items[0];
  const [notes, setNotes] = useState(item.roomNotes ?? "");
  const MAX = 300;

  const update = useMutation({
    mutationFn: () =>
      Promise.all(
        group.items.map((it) =>
          roomsApi.updateReservation(roomId, it.id, {
            checked: it.roomChecked ?? false,
            notes,
          })
        ),
      ),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Stuff Notes - {item.furnitureInfo?.name ?? `Item #${item.id}`}</DialogTitle>
      <DialogContent>
        <TextField
          label="Notes"
          multiline
          minRows={4}
          fullWidth
          inputProps={{ maxLength: MAX }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          helperText={`${notes.length} / ${MAX}`}
          sx={{ mt: 1, mb: 3 }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={update.isPending} onClick={() => update.mutate()}>
            Save
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ─── add placeholder dialog ────────────────────────────────────────────────

interface AddPlaceholderDialogProps {
  open: boolean;
  roomId: number;
  onClose: () => void;
  onCreated: () => void;
}

function AddPlaceholderDialog({ open, roomId, onClose, onCreated }: AddPlaceholderDialogProps) {
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState(1);

  const categories = useQuery({
    queryKey: categoriesKeys.all,
    queryFn: () => inventoryApi.listCategories(),
  });
  const [categoryId, setCategoryId] = useState<number | "">("");

  const create = useMutation({
    mutationFn: () =>
      roomsApi.createItem(roomId, {
        name,
        furnitureCategory: categoryId !== "" ? categoryId : null,
        quantity,
      }),
    onSuccess: () => {
      setName("");
      setCategoryId("");
      setQuantity(1);
      onCreated();
      onClose();
    },
  });

  function handleClose() {
    setName("");
    setCategoryId("");
    setQuantity(1);
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Placeholder</DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          required
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <TextField
          label="Quantity"
          type="number"
          fullWidth
          size="small"
          value={quantity}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!isNaN(v) && v > 0) setQuantity(v);
          }}
          inputProps={{ min: 1 }}
          helperText="One card stands for every slot; fill them from the inventory."
          sx={{ mb: 2 }}
        />
        <Select
          displayEmpty
          fullWidth
          size="small"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          sx={{ mb: 3 }}
        >
          <MenuItem value="">
            <em>Category (optional)</em>
          </MenuItem>
          {(categories.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}
          </MenuItem>)}
        </Select>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Add
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ─── furniture image preview dialog ───────────────────────────────────────

interface ImagePreviewDialogProps {
  name: string;
  src: string;
  onClose: () => void;
}

function ImagePreviewDialog({ name, src, onClose }: ImagePreviewDialogProps) {
  return (
    <Dialog open onClose={onClose} maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        {name}
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ textAlign: "center" }}>
        <Box
          component="img"
          src={src}
          alt={name}
          sx={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
        />
      </DialogContent>
    </Dialog>
  );
}

// ─── furniture item detail dialog ─────────────────────────────────────────

function storageLocationLabel(loc?: StorageLocation | null): string {
  if (!loc) return "—";
  const cat = loc.categoryInfo?.name ?? "Storage";
  const detail = [loc.bin, loc.slot].filter(Boolean).join("-");
  return detail ? `${cat} ${detail}` : cat;
}

interface FurnitureItemDetailDialogProps {
  item: FurnitureItem;
  currentRoom: Room;
  allSameFurnitureItems: FurnitureItem[];
  showMoveToLocation?: boolean;
  onClose: () => void;
  onMoved: () => void;
  onGoToLocation: (stagingId: number, roomId: number) => void;
}

function FurnitureItemDetailDialog({
  item,
  currentRoom,
  allSameFurnitureItems,
  showMoveToLocation = true,
  onClose,
  onMoved,
  onGoToLocation,
}: FurnitureItemDetailDialogProps) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const furniture = item.furnitureInfo;
  const imgSrc = furniture?.imageFile ? thumb(furniture.imageFile.relativePath) : null;
  const itemIds = allSameFurnitureItems.map((i) => i.id);
  const [historyOpen, setHistoryOpen] = useState(false);

  const moveToLocation = useMutation({
    mutationFn: () => updateFurnitureItemsLocations({ itemIds, roomId: currentRoom.id }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roomKeys.detail(currentRoom.id) });
      void qc.invalidateQueries({ queryKey: furnitureKeys.lists() });
      void qc.invalidateQueries({ queryKey: furnitureKeys.detail(item.furniture) });
      onMoved();
      onClose();
    },
  });

  const moveToLocker = useMutation({
    mutationFn: () => updateFurnitureItemsLocations({ itemIds }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roomKeys.detail(currentRoom.id) });
      void qc.invalidateQueries({ queryKey: furnitureKeys.lists() });
      void qc.invalidateQueries({ queryKey: furnitureKeys.detail(item.furniture) });
      onMoved();
      onClose();
    },
  });

  const itemLocation = (it: FurnitureItem): string => {
    if (it.roomInfo) {
      const addr = it.roomInfo.stagingInfo?.address ?? "";
      const room = it.roomInfo.name ?? "";
      return addr ? `${addr} (${room})` : room;
    }
    if (it.locationInfo) {
      const cat = it.locationInfo.categoryInfo?.name ?? "";
      const parts = [cat, it.locationInfo.bin, it.locationInfo.slot].filter(Boolean);
      return parts.join(" ") || "—";
    }
    return "—";
  };
  const isPending = moveToLocation.isPending || moveToLocker.isPending;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth fullScreen={isSmall}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {furniture?.name ?? `Item #${item.id}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {allSameFurnitureItems.length === 1
              ? `#${item.id}`
              : `${allSameFurnitureItems.length} items reserved for ${currentRoom.name}`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {imgSrc
          ? (
            <Box
              component="img"
              src={imgSrc}
              alt={furniture?.name}
              sx={{
                width: "100%",
                maxHeight: 320,
                objectFit: "contain",
                display: "block",
                bgcolor: "grey.50",
              }}
            />
          )
          : (
            <Box
              sx={{
                width: "100%",
                height: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "grey.100",
              }}
            >
              <ImageIcon color="disabled" sx={{ fontSize: 48 }} />
            </Box>
          )}

        <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Current location</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Storage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allSameFurnitureItems.map((it) => {
                const elsewhere = it.roomInfo != null && it.room !== currentRoom.id;
                return (
                  <TableRow key={it.id}>
                    <TableCell>#{it.id}</TableCell>
                    <TableCell>
                      {elsewhere
                        ? (
                          <Typography
                            variant="body2"
                            onClick={() => onGoToLocation(it.roomInfo!.staging, it.roomInfo!.id)}
                            sx={{
                              color: "primary.main",
                              fontWeight: 500,
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {itemLocation(it)}
                          </Typography>
                        )
                        : (
                          <Typography variant="body2">
                            {it.room === currentRoom.id ? "At location" : "In locker"}
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {storageLocationLabel(it.locationInfo)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 1.5, sm: 3 }, pb: 2, flexWrap: "wrap", rowGap: 1 }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => void nav(`/administration/inventory/${item.furniture}`)}
        >
          Edit Furniture
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setHistoryOpen(true)}
        >
          View History
        </Button>
        {showMoveToLocation && (
          <Button
            variant="contained"
            size="small"
            disabled={isPending}
            onClick={() => moveToLocation.mutate()}
          >
            Move to Location
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          disabled={isPending}
          onClick={() => moveToLocker.mutate()}
        >
          Move to Locker
        </Button>
        <Button size="small" onClick={onClose}>Close</Button>
      </DialogActions>
      <ReservationHistoryDialog
        open={historyOpen}
        furnitureId={item.furniture}
        furnitureName={furniture?.name}
        onClose={() => setHistoryOpen(false)}
        onNavigate={onGoToLocation}
      />
    </Dialog>
  );
}

// ─── room content area ─────────────────────────────────────────────────────

interface RoomContentProps {
  room: Room;
  onRoomUpdated: () => void;
  onSelectFurniture: (categoryId: number | "", placeholder?: RoomGenericItem) => void;
  pendingPlaceholder?: RoomGenericItem | null;
  onClearPendingPlaceholder: () => void;
  onGoToLocation: (stagingId: number, roomId: number) => void;
}

function RoomContent(
  {
    room,
    onRoomUpdated,
    onSelectFurniture,
    pendingPlaceholder,
    onClearPendingPlaceholder,
    onGoToLocation,
  }: RoomContentProps,
) {
  const [showFurnitureNotes, setShowFurnitureNotes] = useState(false);
  const [cardScale, setCardScale] = useState<number>(() => readCardScaleCookie());
  const [roomNotesOpen, setRoomNotesOpen] = useState(false);
  const [addPlaceholderOpen, setAddPlaceholderOpen] = useState(false);
  const [editingPlaceholder, setEditingPlaceholder] = useState<RoomGenericItem | null>(null);
  const [editingReserved, setEditingReserved] = useState<ReservedGroup | null>(null);
  const [detailGroup, setDetailGroup] = useState<ReservedGroup | null>(null);
  const [confirmDeletePlaceholder, setConfirmDeletePlaceholder] = useState<
    RoomGenericItem | null
  >(null);
  const [removingGroup, setRemovingGroup] = useState<ReservedGroup | null>(null);
  const qc = useQueryClient();

  const roomDetail = useQuery({
    queryKey: roomKeys.detail(room.id),
    queryFn: () => roomsApi.get(room.id),
  });

  const currentRoom = roomDetail.data ?? room;
  const atLocationItems = currentRoom.furnitureItemHistory ?? [];
  const placeholders = currentRoom.templateAssoc?.items ?? [];
  const reservedGroups = groupReservedItems(atLocationItems, room.id);

  const deleteItem = useMutation({
    mutationFn: (itemId: number) => roomsApi.deleteItem(room.id, itemId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roomKeys.detail(room.id) });
      void qc.invalidateQueries({ queryKey: furnitureKeys.lists() });
      setConfirmDeletePlaceholder(null);
    },
  });

  const checkItem = useMutation({
    mutationFn: ({ item, checked }: { item: RoomGenericItem; checked: boolean }) =>
      roomsApi.updateItem(room.id, item.id, {
        name: item.name,
        notes: item.notes,
        quantity: item.quantity,
        checked,
        replaceItem: item.replaceItem,
        furnitureCategory: item.furnitureCategory,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: roomKeys.detail(room.id) }),
  });

  const unreserve = useMutation({
    mutationFn: (furnitureItemIds: number[]) => unreserveFurnitureItems(room.id, furnitureItemIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roomKeys.detail(room.id) });
      void qc.invalidateQueries({ queryKey: furnitureKeys.lists() });
      setRemovingGroup(null);
    },
  });

  const checkReservedItems = useMutation({
    mutationFn: ({ items, checked }: { items: FurnitureItem[]; checked: boolean }) =>
      Promise.all(
        items.map((item) =>
          roomsApi.updateReservation(room.id, item.id, {
            checked,
            notes: item.roomNotes ?? "",
          })
        ),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: roomKeys.detail(room.id) }),
  });

  // persist card size across refreshes
  useEffect(() => {
    writeCardScaleCookie(cardScale);
  }, [cardScale]);

  return (
    <Box
      sx={{ flex: 1, overflow: "auto", p: { xs: 1, sm: 2 } }}
      onClick={onClearPendingPlaceholder}
    >
      {/* room header: name and toolbar share a wrapping row, notes sit beneath */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              flex: "1 1 auto",
              minWidth: 140,
              textTransform: "uppercase",
              fontWeight: 700,
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          >
            {currentRoom.name}
          </Typography>

          {/* toolbar — wraps onto its own line before the buttons ever squash */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: "wrap",
              rowGap: 1,
              flexGrow: 1,
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
            useFlexGap
          >
            <Button
              size="small"
              variant={showFurnitureNotes ? "contained" : "outlined"}
              startIcon={<SpeakerNotesIcon />}
              onClick={() => setShowFurnitureNotes((v) => !v)}
              color={showFurnitureNotes ? "primary" : "inherit"}
              sx={{ textTransform: "none" }}
            >
              Stuff Notes
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<NoteIcon />}
              onClick={() => setRoomNotesOpen(true)}
              sx={{ textTransform: "none" }}
            >
              Room Notes
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setAddPlaceholderOpen(true)}
              sx={{ textTransform: "none" }}
            >
              Placeholder
            </Button>
            <Tooltip title="Resize items">
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 160, px: 1 }}>
                <ImageIcon fontSize="small" color="action" />
                <Slider
                  size="small"
                  value={cardScale}
                  min={CARD_SCALE_MIN}
                  max={CARD_SCALE_MAX}
                  step={0.1}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
                  onChange={(_, v) => setCardScale(v)}
                  aria-label="Resize items"
                />
              </Stack>
            </Tooltip>
          </Stack>
        </Box>

        {currentRoom.notes && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
          >
            {currentRoom.notes}
          </Typography>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {roomDetail.isLoading && <CircularProgress size={24} />}

      {/* items grid */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {/* reserved items first, narrowest first — one card per furniture + note */}
        {reservedGroups.map((group) => {
          const item = group.items[0];
          const allChecked = group.items.every((i) => i.roomChecked);
          const someChecked = group.items.some((i) => i.roomChecked);
          return (
            <ReservedCard
              key={`g-${group.key}`}
              item={item}
              quantity={group.items.length}
              checked={allChecked}
              indeterminate={someChecked && !allChecked}
              showNotes={showFurnitureNotes}
              scale={cardScale}
              onDelete={() => setRemovingGroup(group)}
              onCheck={(checked) => checkReservedItems.mutate({ items: group.items, checked })}
              onEditNotes={() => setEditingReserved(group)}
              onImageClick={() => setDetailGroup(group)}
              statusBanner={<LocationBreakdown counts={group.counts} />}
            />
          );
        })}

        {/* placeholder items, after everything already reserved */}
        {placeholders.map((item) => (
          <PlaceholderCard
            key={`ph-${item.id}`}
            item={item}
            showNotes={showFurnitureNotes}
            scale={cardScale}
            isSelected={item.id === pendingPlaceholder?.id}
            onDelete={() => setConfirmDeletePlaceholder(item)}
            onCheck={(checked) => checkItem.mutate({ item, checked })}
            onEditNotes={() => setEditingPlaceholder(item)}
            onSelectFurniture={() => onSelectFurniture(item.furnitureCategory ?? "", item)}
          />
        ))}

        {placeholders.length === 0 && atLocationItems.length === 0 && !roomDetail.isLoading && (
          <Typography color="text.secondary" variant="body2">
            No items yet. Reserve furniture from the inventory tab or add a placeholder.
          </Typography>
        )}
      </Box>

      {/* dialogs */}
      {roomNotesOpen && (
        <RoomNotesDialog
          room={currentRoom}
          onClose={() => setRoomNotesOpen(false)}
          onSaved={() => {
            onRoomUpdated();
            void qc.invalidateQueries({ queryKey: roomKeys.detail(room.id) });
          }}
        />
      )}
      {addPlaceholderOpen && (
        <AddPlaceholderDialog
          open
          roomId={room.id}
          onClose={() => setAddPlaceholderOpen(false)}
          onCreated={() => void qc.invalidateQueries({ queryKey: roomKeys.detail(room.id) })}
        />
      )}
      {editingPlaceholder && (
        <PlaceholderNotesDialog
          roomId={room.id}
          item={editingPlaceholder}
          onClose={() => setEditingPlaceholder(null)}
          onSaved={() => void qc.invalidateQueries({ queryKey: roomKeys.detail(room.id) })}
        />
      )}
      {editingReserved && (
        <ReservedNotesDialog
          roomId={room.id}
          group={editingReserved}
          onClose={() => setEditingReserved(null)}
          onSaved={() => void qc.invalidateQueries({ queryKey: roomKeys.detail(room.id) })}
        />
      )}
      {detailGroup && (
        <FurnitureItemDetailDialog
          item={detailGroup.items[0]}
          currentRoom={currentRoom}
          allSameFurnitureItems={detailGroup.items}
          showMoveToLocation={detailGroup.counts.atLocation < detailGroup.items.length}
          onClose={() => setDetailGroup(null)}
          onMoved={() => setDetailGroup(null)}
          onGoToLocation={onGoToLocation}
        />
      )}
      {removingGroup && (
        <RemoveReservedDialog
          group={removingGroup}
          isPending={unreserve.isPending}
          onClose={() => setRemovingGroup(null)}
          onConfirm={(quantity) =>
            unreserve.mutate(removingGroup.items.slice(0, quantity).map((i) => i.id))}
        />
      )}
      <ConfirmDialog
        open={confirmDeletePlaceholder !== null}
        title="Remove placeholder"
        message={confirmDeletePlaceholder && confirmDeletePlaceholder.quantity > 1
          ? `Remove all ${confirmDeletePlaceholder.quantity} "${confirmDeletePlaceholder.name}" slots from the room?`
          : "Are you sure you want to remove this item from the room?"}
        onClose={() => setConfirmDeletePlaceholder(null)}
        isPending={deleteItem.isPending}
        onConfirm={() => {
          if (confirmDeletePlaceholder) deleteItem.mutate(confirmDeletePlaceholder.id);
        }}
      />
    </Box>
  );
}

// ─── main page ─────────────────────────────────────────────────────────────
type RoomContext = {
  stagingId: number;
  roomId: number | null;
};

export function RoomBuilderPage() {
  const { id, roomId: roomIdParam } = useParams<{ id: string; roomId?: string }>();
  const stagingIdFromParam = Number(id);
  const targetRoomIdFromParam = roomIdParam ? Number(roomIdParam) : null;

  const [roomContext, setRoomContext] = useState<RoomContext>({
    stagingId: stagingIdFromParam,
    roomId: targetRoomIdFromParam,
  });

  const stagingId = roomContext.stagingId;
  const targetRoomId = roomContext.roomId;

  const nav = useNavigate();
  const qc = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [sidebarTab, setSidebarTab] = useState(0); // 0=Rooms, 1=Inventory
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [inventoryCategoryId, setInventoryCategoryId] = useState<number | "">("");
  const [pendingPlaceholder, setPendingPlaceholder] = useState<RoomGenericItem | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const staging = useQuery({
    queryKey: stagingsKeys.detail(stagingId),
    queryFn: () => stagingsApi.get(stagingId),
    enabled: !Number.isNaN(stagingId),
  });

  const updateStagingStatus = useMutation({
    mutationFn: (status: number) => stagingsApi.patchStatus(stagingId, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stagingsKeys.detail(stagingId) });
      void qc.invalidateQueries({ queryKey: stagingsKeys.lists() });
      void qc.invalidateQueries({ queryKey: stagingsKeys.summaries() });
    },
  });

  const rooms = useQuery({
    queryKey: roomsKeys.list(stagingId),
    queryFn: () => roomsApi.list(stagingId),
    enabled: !Number.isNaN(stagingId),
  });

  const onGoToLocation = (stagingId: number, roomId: number) => {
    setRoomContext({
      stagingId: stagingId,
      roomId: roomId,
    });
    setSelectedRoom(null);
  };

  // Auto-select room when list loads: prefer URL roomId param, fall back to first room
  const roomList = rooms.data ?? [];
  if (roomList.length > 0 && (selectedRoom === null)) {
    const target = targetRoomId
      ? (roomList.find((r) => r.id === targetRoomId) ?? roomList[0])
      : roomList[0];
    setSelectedRoom(target);
  }

  useEffect(() => {
    void qc.invalidateQueries({ queryKey: roomsKeys.list(stagingId) });
    void qc.invalidateQueries({ queryKey: stagingsKeys.detail(stagingId) });
    if (targetRoomId != null) {
      void qc.invalidateQueries({ queryKey: roomKeys.detail(targetRoomId) });
    }
  }, [stagingId, targetRoomId, qc]);

  // Keep URL in sync with selected room
  useEffect(() => {
    if (selectedRoom) {
      void nav(`/staging/${stagingId}/room-builder/${selectedRoom.id}`, { replace: true });
    }
  }, [selectedRoom, stagingId, nav]);

  if (staging.isLoading || rooms.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const stagingData = staging.data;

  const handleSelectRoom = (r: Room) => {
    setSelectedRoom(r);
    if (isMobile) setMobileSidebarOpen(false);
  };

  const sidebarContent = (
    <>
      <Tabs
        value={sidebarTab}
        onChange={(_, v) => {
          setSidebarTab(v);
          if (v !== 1) setPendingPlaceholder(null);
        }}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Rooms" sx={{ minHeight: 40, py: 0.5, fontSize: 12 }} />
        <Tab label="Inventory" sx={{ minHeight: 40, py: 0.5, fontSize: 12 }} />
      </Tabs>

      {sidebarTab === 0
        ? (
          <RoomsSidebar
            stagingId={stagingId}
            rooms={roomList}
            selectedRoomId={selectedRoom?.id ?? null}
            onSelect={handleSelectRoom}
            onRefresh={() =>
              setSelectedRoom((prev) => (roomList.find((r) => r.id === prev?.id) ?? null))}
          />
        )
        : (staging.data && (
          <InventoryPanel
            staging={staging.data}
            selectedRoom={selectedRoom}
            pendingPlaceholder={pendingPlaceholder}
            onReserved={() => {
              setPendingPlaceholder(null);
              if (selectedRoom) {
                void qc.invalidateQueries({ queryKey: roomKeys.detail(selectedRoom.id) });
              }
            }}
            categoryId={inventoryCategoryId}
            onCategoryChange={setInventoryCategoryId}
          />
        ))}
    </>
  );

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 64px)", overflow: "hidden" }}>
      {/* ── left: room content ── */}
      <Box
        sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}
      >
        {/* sub-header */}
        <Box
          sx={{
            px: { xs: 1, sm: 2 },
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              flex: 1,
              minWidth: 120,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          >
            {stagingData?.address}
          </Typography>
          {stagingData && (
            <Select
              size="small"
              value={stagingData.status ?? StagingStatus.Upcoming}
              onChange={(e) => updateStagingStatus.mutate(Number(e.target.value))}
              disabled={updateStagingStatus.isPending}
              sx={{ minWidth: { xs: 110, sm: 140 } }}
            >
              {Object.entries(STAGING_STATUS).map(([value, label]) => (
                <MenuItem key={value} value={Number(value)}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          )}
          <Button variant="contained" size="small" onClick={() => void nav("/")}>
            Back
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => void nav(`/staging/${stagingId}`)}
          >
            List
          </Button>
          {isMobile && (
            <Tooltip title="Open panel">
              <IconButton onClick={() => setMobileSidebarOpen(true)} size="small" edge="end">
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* room content or empty state */}
        {selectedRoom
          ? (
            <RoomContent
              key={selectedRoom.id}
              room={selectedRoom}
              onRoomUpdated={() =>
                void qc.invalidateQueries({ queryKey: roomsKeys.list(stagingId) })}
              onSelectFurniture={(catId, placeholder) => {
                setInventoryCategoryId(catId);
                setSidebarTab(1);
                setPendingPlaceholder(placeholder ?? null);
                if (isMobile) setMobileSidebarOpen(true);
              }}
              pendingPlaceholder={pendingPlaceholder}
              onClearPendingPlaceholder={() => setPendingPlaceholder(null)}
              onGoToLocation={onGoToLocation}
            />
          )
          : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
                textAlign: "center",
              }}
            >
              <Stack spacing={2} alignItems="center">
                <Typography color="text.secondary">Select a room from the sidebar</Typography>
                {isMobile && (
                  <Button
                    variant="contained"
                    startIcon={<MenuIcon />}
                    onClick={() => setMobileSidebarOpen(true)}
                  >
                    Open panel
                  </Button>
                )}
              </Stack>
            </Box>
          )}
      </Box>

      {/* ── right: sidebar (persistent on desktop, drawer on mobile) ── */}
      {isMobile
        ? (
          <Drawer
            anchor="right"
            open={mobileSidebarOpen}
            onClose={() => setMobileSidebarOpen(false)}
            PaperProps={{
              sx: {
                width: "85%",
                maxWidth: 320,
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 1,
                py: 0.5,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2" sx={{ flex: 1, pl: 1 }}>
                Panel
              </Typography>
              <IconButton size="small" onClick={() => setMobileSidebarOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {sidebarContent}
          </Drawer>
        )
        : (
          <Box
            sx={{
              width: 220,
              flexShrink: 0,
              borderLeft: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
            }}
          >
            {sidebarContent}
          </Box>
        )}
    </Box>
  );
}
